import React from 'react';
import {
  getGridDateOperators,
  getGridNumericOperators,
  getGridSingleSelectOperators,
  getGridStringOperators
} from '@mui/x-data-grid-premium';
import { isEmpty } from 'lodash';

import { TaskIdFilterSelector } from 'components/pages/patient-source/PmWorkbook/PMWorkbookTable/CustomFilterOperators/CustomFilterSelectors/TaskIdFilterSelector';
import { TaskStatusFilterSelector } from 'components/pages/patient-source/PmWorkbook/PMWorkbookTable/CustomFilterOperators/CustomFilterSelectors/TaskStatusFilterSelector';
import { TaskTopicFilterSelector } from 'components/pages/patient-source/PmWorkbook/PMWorkbookTable/CustomFilterOperators/CustomFilterSelectors/TaskTopicFilterSelector';
import { tasksStatusesMap } from 'components/root/Container/Layout/Tasks/Task/taskConstants';

const allStringOperators = getGridStringOperators();
const allNumberOperators = getGridNumericOperators();
export const allSingleSelectOperators = getGridSingleSelectOperators();
export const allDateOperators = getGridDateOperators();

const getFilterValueAsStringForStatusAndTopic = value => {
  if (value.operator === 'is any of') {
    return `${value?.operator || ''} ${value?.multipleFilterValue?.join(', ') || ''}`;
  }
  return `${value?.operator || ''} ${value?.filterValue || ''}`;
};

const taskIdFilterOperators = {
  label: 'Task ID',
  value: 'taskId',
  getValueAsString: value => `${value?.operator || ''} ${value?.filterValue || ''}`,
  getApplyFilterFn: filterItem => {
    const containsOperatorSelected = filterItem.value?.operator === 'contains';
    if (
      !filterItem.field ||
      !filterItem.value?.operator ||
      (containsOperatorSelected && !filterItem.value.filterValue) ||
      !filterItem.operator
    ) {
      return null;
    }
    return (value, row, column) => {
      const allTaskIds = row?.[column.field]?.map(task => task.sequenceNumber) ?? [];
      if (filterItem.value.operator === 'contains') {
        return allTaskIds.some(taskId => taskId?.toString().includes(filterItem.value.filterValue));
      } else if (filterItem.value.operator === 'is empty') {
        return allTaskIds.some(taskId => !taskId || taskId.toString().trim() === '');
      } else if (filterItem.value.operator === 'is not empty') {
        return allTaskIds.some(taskId => taskId && taskId.toString().trim() !== '');
      } else {
        return false;
      }
    };
  },
  InputComponent: props => <TaskIdFilterSelector {...props} />
};

const taskStatusFilterOperators = {
  label: 'Task Status',
  value: 'taskStatus',
  getValueAsString: getFilterValueAsStringForStatusAndTopic,
  getApplyFilterFn: filterItem => {
    const isAnyOfOperatorSelected = filterItem.value?.operator === 'is any of';
    if (
      !filterItem.field ||
      !filterItem.value?.operator ||
      (isAnyOfOperatorSelected && isEmpty(filterItem.value.multipleFilterValue)) ||
      (!isAnyOfOperatorSelected && !filterItem.value.filterValue) ||
      !filterItem.operator
    ) {
      return null;
    }

    return (value, row, column) => {
      const allTaskStatuses = row?.[column.field]?.map(task => tasksStatusesMap[task.status]?.name) ?? [];
      if (filterItem.value.operator === 'is') {
        return allTaskStatuses.some(status => status === filterItem.value.filterValue);
      } else if (filterItem.value.operator === 'is not') {
        return isEmpty(allTaskStatuses) || allTaskStatuses.some(status => status !== filterItem.value.filterValue);
      } else if (filterItem.value.operator === 'is any of') {
        return allTaskStatuses.some(status => filterItem.value.multipleFilterValue.includes(status));
      } else {
        return false;
      }
    };
  },
  InputComponent: props => <TaskStatusFilterSelector {...props} />
};

export const patientIdHasDuplicateFilterOperators = {
  label: 'is duplicated',
  value: 'duplicated',
  getApplyFilterFn: filterItem => {
    if (!filterItem.operator || !filterItem.field) {
      return null;
    }
    return (value, row) => {
      return row.patientIdDuplicated;
    };
  },
  requiresFilterValue: false
};

const taskTopicFilterOperators = {
  label: 'Task Topic',
  value: 'taskTopic',
  getValueAsString: getFilterValueAsStringForStatusAndTopic,
  getApplyFilterFn: filterItem => {
    const isAnyOfOperatorSelected = filterItem.value?.operator === 'is any of';
    if (
      !filterItem.field ||
      !filterItem.value?.operator ||
      (isAnyOfOperatorSelected && isEmpty(filterItem.value.multipleFilterValue)) ||
      (!isAnyOfOperatorSelected && !filterItem.value.filterValue) ||
      !filterItem.operator
    ) {
      return null;
    }

    return (value, row, column) => {
      const allTaskTopics = row?.[column.field]?.map(task => task.topicName) ?? [];
      if (filterItem.value.operator === 'is') {
        return allTaskTopics.some(status => status === filterItem.value.filterValue);
      } else if (filterItem.value.operator === 'is not') {
        return isEmpty(allTaskTopics) || allTaskTopics.some(status => status !== filterItem.value.filterValue);
      } else if (filterItem.value.operator === 'is any of') {
        return allTaskTopics.some(status => filterItem.value.multipleFilterValue.includes(status));
      } else {
        return false;
      }
    };
  },
  InputComponent: props => <TaskTopicFilterSelector {...props} />
};

export const containsStandardOperator = allStringOperators.filter(op => op.value === 'contains');
const isEmptyStandardOperator = allStringOperators.filter(op => op.value === 'isEmpty');
const isNotEmptyStandardOperator = allStringOperators.filter(op => op.value === 'isNotEmpty');
const startWithEndWithStandardOperators = allStringOperators.filter(
  op => op.value === 'startsWith' || op.value === 'endsWith'
);

const isOperatorSingleSelect = allSingleSelectOperators.filter(op => op.value === 'is');

export const allStandardNumberOperatorsWithoutIsAnyOf = allNumberOperators.filter(op => op.value !== 'isAnyOf');
export const isEmptyIsNotEmpty = allStringOperators.filter(op => op.value === 'isEmpty' || op.value === 'isNotEmpty');
export const containsIsEmptyIsNotEmpty = allStringOperators.filter(op =>
  ['contains', 'isEmpty', 'isNotEmpty'].includes(op.value)
);
export const isIsEmptyIsNotEmptySingleSelect = [
  isOperatorSingleSelect[0],
  isEmptyStandardOperator[0],
  isNotEmptyStandardOperator[0]
];
export const isIsNotIsAnyOfIsEmptyIsNotEmptySingleSelect = [...allSingleSelectOperators, ...isEmptyIsNotEmpty];
export const containsIsIsNotIsAnyOfStartsWithEndWithIsEmptyIsNotEmptySingleSelect = [
  containsStandardOperator[0],
  ...allSingleSelectOperators,
  ...startWithEndWithStandardOperators,
  ...isEmptyIsNotEmpty
];
export const taskOperators = [taskIdFilterOperators, taskStatusFilterOperators, taskTopicFilterOperators];
